import React from 'react'
import { faBookmark, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled, { css } from 'styled-components'
import theme, { institutionalColors } from '~/design'
import Link from '~/prix/react/components/link'
import { PulseLoader } from 'react-spinners'

const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 100%;
  background: #fff;
  border: 1px solid #e0e5eb;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadow(1)};
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }
`

const FixedIconWrapper = styled.div`
  position: absolute;
  top: -12px;
  right: 12px;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`

const ImageWrapper = styled.div`
  height: 165px;
  flex: 0 0 165px;
  margin: 12px 12px 0 12px;
  border-radius: 5px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 5px;
  padding: 12px;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 16px;
    font-weight: 700;
    line-height: 1.15;
    margin-bottom: 5px;
  }

  p {
    margin: 0;
    color: ${institutionalColors.graySebrae45};
    font-size: 14px;
    line-height: 1.15;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
  padding-top: 8px;
  gap: 8px;
`

const cardLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  color: ${({ theme }) => theme.colors.darkGrey};
  border: 1px solid ${({ theme }) => theme.colors.darkGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  transition: all 0.2s ease-in-out;

  svg,
  svg > * {
    color: ${({ theme }) => theme.colors.darkGrey};
    height: 12px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};

    svg,
    svg > * {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const CardLink = styled(Link)`
  ${cardLinkStyle}
`

const CardLinkSecondary = styled.button`
  ${cardLinkStyle}
  background: none;
  border: none;
  cursor: pointer;

  svg,
  svg > * {
    height: 16px;
  }
`

export type ReportsAndStudiesCardProperties = {
  title: string
  imageSource: string
  imageAlternativeText: string
  description: string
  destinationPath: string
  isFixed?: boolean
  isChangingFixedState?: boolean
  onChangeFixedState?: (isFixed: boolean) => void
}

export default function ReportsAndStudiesCard({
  title,
  imageSource,
  imageAlternativeText,
  description,
  destinationPath,
  isFixed = false,
  isChangingFixedState = false,
  onChangeFixedState,
}: ReportsAndStudiesCardProperties) {
  return (
    <Card>
      {isFixed && (
        <FixedIconWrapper>
          <FontAwesomeIcon icon={faBookmark} />
        </FixedIconWrapper>
      )}
      <ImageWrapper>
        <img src={imageSource} alt={imageAlternativeText} />
      </ImageWrapper>
      <Body>
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <Footer>
          {onChangeFixedState && (
            <CardLinkSecondary
              type='button'
              aria-pressed={isFixed}
              aria-label={isFixed ? 'Desafixar relatório ou estudo' : 'Fixar relatório ou estudo'}
              onClick={() => onChangeFixedState(!isFixed)}
              disabled={isChangingFixedState}
            >
              {isChangingFixedState ? (
                <PulseLoader color={theme.colors.darkGrey} size={6} />
              ) : (
                <>
                  {isFixed ? 'desafixar' : 'fixar'}
                  <FontAwesomeIcon icon={isFixed ? faBookmark : faBookmarkRegular} />
                </>
              )}
            </CardLinkSecondary>
          )}
          <CardLink href={destinationPath}>
            ver mais
            <FontAwesomeIcon icon={faChevronRight} />
          </CardLink>
        </Footer>
      </Body>
    </Card>
  )
}
